import { useIntl } from '@umijs/max';
import { Empty, Modal, Spin, Tag, Typography } from 'antd';
import { createStyles } from 'antd-style';
import dayjs from 'dayjs';
import React from 'react';
import UserCell from '@/components/UserCell';
import { useUserMap } from '@/hooks';
import {
  type AssetItem,
  type AssetMediaType,
  type AssetSource,
  listAdminAssetImages,
} from '@/services/drama/adminAssets';
import { MediaPreviewDialog } from '@/shared/components';
import { TouchSafeTooltip as Tooltip } from '@/shared/components/TouchSafeTooltip';

const { Text } = Typography;

const useStyles = createStyles(({ token, css }) => ({
  toolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 12px;
    min-height: 32px;
    flex-wrap: wrap;
  `,
  grid: css`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  `,
  card: css`
    display: flex;
    flex-direction: column;
    border-radius: ${token.borderRadiusLG}px;
    overflow: hidden;
    background: ${token.colorFillTertiary};
    border: 1px solid ${token.colorBorderSecondary};
    transition:
      border-color 0.15s,
      background-color 0.15s;
    &:hover {
      border-color: ${token.colorBorder};
      background: ${token.colorFillQuaternary};
    }
  `,
  thumbWrap: css`
    position: relative;
    width: 100%;
    aspect-ratio: 1 / 1;
    background: ${token.colorFillTertiary};
    overflow: hidden;
    .ant-image,
    .ant-image-img,
    img,
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
  `,
  thumbOverlay: css`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.92);
    background: linear-gradient(
      180deg,
      transparent 40%,
      rgba(0, 0, 0, 0.45) 100%
    );
    cursor: pointer;
    pointer-events: auto;
    border: none;
    padding: 0;
    font: inherit;
  `,
  playIcon: css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(0, 0, 0, 0.55);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  `,
  durationChip: css`
    position: absolute;
    right: 6px;
    bottom: 6px;
    background: rgba(0, 0, 0, 0.65);
    color: ${token.colorTextLightSolid};
    font-size: 11px;
    line-height: 1;
    padding: 3px 6px;
    border-radius: 999px;
  `,
  audioBackdrop: css`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(
      135deg,
      ${token.colorFillTertiary} 0%,
      ${token.colorFillQuaternary} 100%
    );
    color: ${token.colorTextSecondary};
    font-size: 32px;
    cursor: pointer;
    border: none;
    padding: 0;
    font: inherit;
  `,
  imageMissing: css`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${token.colorTextQuaternary};
    font-size: 12px;
  `,
  meta: css`
    padding: 6px 8px 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: ${token.colorBgContainer};
  `,
  metaRow: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    font-size: 11px;
    color: ${token.colorTextSecondary};
    min-height: 18px;
  `,
  sourceTag: css`
    margin: 0;
    font-size: 10px;
    line-height: 16px;
    padding: 0 6px;
  `,
  promptLine: css`
    font-size: 12px;
    color: ${token.colorText};
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-all;
    min-height: 32px;
  `,
  pageBar: css`
    margin-top: 16px;
    display: flex;
    justify-content: flex-end;
  `,
  emptyWrap: css`
    padding: 64px 0;
  `,
  sentinel: css`
    width: 100%;
    height: 1px;
    margin-top: 16px;
  `,
  playerVideo: css`
    width: 100%;
    max-height: 70vh;
    background: #000;
    border-radius: ${token.borderRadius}px;
  `,
  playerAudio: css`
    width: 100%;
  `,
  playerPrompt: css`
    margin-top: 12px;
    font-size: 12px;
    color: ${token.colorTextSecondary};
    word-break: break-all;
    max-height: 120px;
    overflow: auto;
  `,
}));

const SOURCE_COLOR: Record<AssetSource, string> = {
  canvas_image: 'blue',
  canvas_video: 'geekblue',
  canvas_audio: 'volcano',
  character_shot: 'purple',
  character_sheet: 'magenta',
  character_canonical: 'cyan',
  project_cover: 'green',
  scene_canonical: 'gold',
  scene_panorama: 'orange',
};

const SOURCE_LABEL_DEFAULT: Record<AssetSource, string> = {
  canvas_image: '画布生图',
  canvas_video: '画布视频',
  canvas_audio: '画布配音',
  character_shot: '角色出图',
  character_sheet: '角色 Sheet',
  character_canonical: '角色主图',
  project_cover: '剧本封面',
  scene_canonical: '场景主图',
  scene_panorama: '场景全景',
};

const SOURCE_LABEL_ID: Record<AssetSource, string> = {
  canvas_image: 'pages.assets.source.canvasImage',
  canvas_video: 'pages.assets.source.canvasVideo',
  canvas_audio: 'pages.assets.source.canvasAudio',
  character_shot: 'pages.assets.source.characterShot',
  character_sheet: 'pages.assets.source.characterSheet',
  character_canonical: 'pages.assets.source.characterCanonical',
  project_cover: 'pages.assets.source.projectCover',
  scene_canonical: 'pages.assets.source.sceneCanonical',
  scene_panorama: 'pages.assets.source.scenePanorama',
};

interface UserAssetsPanelProps {
  ownerId?: string;
  onOpenUser?: (numericUserId: number) => void;
}

const PAGE_SIZE = 48;

function formatDuration(sec?: number): string {
  if (!sec || sec <= 0) return '';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

const UserAssetsPanel: React.FC<UserAssetsPanelProps> = ({ ownerId, onOpenUser }) => {
  const intl = useIntl();
  const { styles } = useStyles();

  const [items, setItems] = React.useState<AssetItem[]>([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(1);
  const [loadingInitial, setLoadingInitial] = React.useState(false);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [playerItem, setPlayerItem] = React.useState<AssetItem | null>(null);
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const showOwner = !ownerId;

  const fetchList = React.useCallback(async (nextPage: number, append = false) => {
    if (append) setLoadingMore(true);
    else setLoadingInitial(true);
    try {
      const r = await listAdminAssetImages({
        user_id: ownerId,
        page: nextPage,
        page_size: PAGE_SIZE,
      });
      if (r?.success && r.data) {
        const nextItems: AssetItem[] = r.data.items || [];
        setItems((prev) =>
          append
            ? [
                ...prev,
                ...nextItems.filter((it) => !prev.some((p) => p.id === it.id)),
              ]
            : nextItems,
        );
        setTotal(r.data.total || 0);
        setPage(nextPage);
      } else if (!append) {
        setItems([]);
        setTotal(0);
      }
    } catch {
      if (!append) {
        setItems([]);
        setTotal(0);
      }
    } finally {
      if (append) setLoadingMore(false);
      else setLoadingInitial(false);
    }
  }, [ownerId]);

  React.useEffect(() => {
    fetchList(1, false);
  }, [fetchList]);

  const hasMore = items.length < total;
  React.useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      if (loadingInitial || loadingMore || !hasMore) return;
      fetchList(page + 1, true);
    }, { rootMargin: '320px 0px' });
    obs.observe(el);
    return () => obs.disconnect();
  }, [fetchList, hasMore, loadingInitial, loadingMore, page]);

  const userIds = React.useMemo(
    () => (showOwner ? items.map((i) => i.user_id) : []),
    [items, showOwner],
  );
  const { userMap } = useUserMap(userIds);

  // 渲染缩略图。Image / Video 统一走 MediaPreviewDialog;Audio 保留原生播放弹窗。
  const renderThumb = (it: AssetItem) => {
    if (it.media_type === 'image') {
      return it.url ? (
        <button
          type="button"
          className={styles.thumbOverlay}
          onClick={() => setPlayerItem(it)}
          aria-label="preview"
          style={{ background: 'transparent' }}
        >
          <img src={it.url} alt="" />
        </button>
      ) : (
        <div className={styles.imageMissing}>—</div>
      );
    }
    if (it.media_type === 'video') {
      return (
        <>
          {it.poster_url ? (
            <img src={it.poster_url} alt="" />
          ) : (
            <button
              type="button"
              className={styles.audioBackdrop}
              onClick={() => setPlayerItem(it)}
            >
              ▶
            </button>
          )}
          <button
            type="button"
            className={styles.thumbOverlay}
            onClick={() => setPlayerItem(it)}
            aria-label="play"
          >
            <span className={styles.playIcon}>▶</span>
          </button>
          {it.duration ? (
            <span className={styles.durationChip}>
              {formatDuration(it.duration)}
            </span>
          ) : null}
        </>
      );
    }
    // audio
    return (
      <button
        type="button"
        className={styles.audioBackdrop}
        onClick={() => setPlayerItem(it)}
      >
        ♪
      </button>
    );
  };

  return (
    <>
      <div className={styles.toolbar}>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {intl.formatMessage(
            { id: 'pages.assets.totalCount', defaultMessage: '共 {n} 项' },
            { n: total },
          )}
        </Text>
      </div>

      <Spin spinning={loadingInitial}>
        {items.length === 0 && !loadingInitial ? (
          <div className={styles.emptyWrap}>
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
          </div>
        ) : (
          <div className={styles.grid}>
              {items.map((it) => {
                const sourceLabel = intl.formatMessage({
                  id: SOURCE_LABEL_ID[it.source],
                  defaultMessage: SOURCE_LABEL_DEFAULT[it.source],
                });
                return (
                  <div key={it.id} className={styles.card}>
                    <div className={styles.thumbWrap}>{renderThumb(it)}</div>
                    <div className={styles.meta}>
                      {it.prompt ? (
                        <Tooltip title={it.prompt}>
                          <div className={styles.promptLine}>{it.prompt}</div>
                        </Tooltip>
                      ) : (
                        <div className={styles.promptLine}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            —
                          </Text>
                        </div>
                      )}
                      <div className={styles.metaRow}>
                        <Tag
                          color={SOURCE_COLOR[it.source]}
                          className={styles.sourceTag}
                        >
                          {sourceLabel}
                        </Tag>
                        <span>
                          {dayjs(it.created_at).format('MM-DD HH:mm')}
                        </span>
                      </div>
                      {showOwner && (
                        <div className={styles.metaRow}>
                          <UserCell
                            userId={it.user_id}
                            userMap={userMap}
                            onOpen={onOpenUser}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </Spin>

      {loadingMore && (
        <div style={{ padding: '16px 0', textAlign: 'center' }}>
          <Spin size="small" />
        </div>
      )}
      <div ref={sentinelRef} className={styles.sentinel} aria-hidden="true" />

      <Modal
        open={playerItem?.media_type === 'audio'}
        onCancel={() => setPlayerItem(null)}
        footer={null}
        width={480}
        centered
        destroyOnHidden
        title={null}
      >
        {playerItem?.media_type === 'audio' && (
          // biome-ignore lint/a11y/useMediaCaption: admin internal preview, no captions track
          <audio
            className={styles.playerAudio}
            src={playerItem.url}
            controls
            autoPlay
          />
        )}
        {playerItem?.prompt && (
          <div className={styles.playerPrompt}>{playerItem.prompt}</div>
        )}
      </Modal>
      <MediaPreviewDialog
        open={playerItem?.media_type === 'video' || playerItem?.media_type === 'image'}
        onClose={() => setPlayerItem(null)}
        url={playerItem?.media_type === 'video' || playerItem?.media_type === 'image' ? playerItem.url : undefined}
        type={playerItem?.media_type === 'video' ? 'video' : 'image'}
        title={playerItem?.prompt}
        filenamePrefix={playerItem?.source}
      />
    </>
  );
};

export default UserAssetsPanel;
